import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

/**
 * Generic fuzzy-search dropdown.
 * Props:
 *  - options: [{ value, label, sublabel?, badge?, keywords? }]
 *  - value: current selected value (or '')
 *  - onChange: (newValue) => void
 *  - placeholder
 *  - emptyMsg
 *  - allowClear (default true): show "Aucun" option
 *  - testId
 */
export const SearchableSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Sélectionner...',
    emptyMsg = 'Aucun résultat',
    allowClear = true,
    disabled = false,
    testId,
    triggerClassName = '',
}) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    className={`w-full justify-between h-11 rounded-sm font-normal ${triggerClassName}`}
                    data-testid={testId}
                >
                    <span className="truncate">
                        {selected ? (
                            <>
                                {selected.label}
                                {selected.sublabel && <span className="text-[#52525B] ml-1 text-xs">· {selected.sublabel}</span>}
                                {selected.badge && (
                                    <span className="ml-2 text-[10px] uppercase font-bold bg-[#E85A24]/10 text-[#E85A24] px-1.5 py-0.5 rounded-sm">
                                        {selected.badge}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-[#52525B]">{placeholder}</span>
                        )}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                        {allowClear && value && (
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); onChange(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange(''); } }}
                                className="text-[#52525B] hover:text-[#0A0A0A] cursor-pointer p-0.5 rounded-sm"
                            >
                                <X className="h-3 w-3" />
                            </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-[#52525B]" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                <Command
                    filter={(itemValue, search) => {
                        // Fuzzy: every space-separated token of search must be a substring of itemValue
                        if (!search) return 1;
                        const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
                        const haystack = (itemValue || '').toLowerCase();
                        return tokens.every((t) => haystack.includes(t)) ? 1 : 0;
                    }}
                >
                    <CommandInput placeholder="Rechercher..." className="h-9" data-testid={testId ? `${testId}-input` : undefined} />
                    <CommandList>
                        <CommandEmpty>{emptyMsg}</CommandEmpty>
                        <CommandGroup>
                            {options.map((opt) => {
                                const searchValue = `${opt.label || ''} ${opt.sublabel || ''} ${opt.badge || ''} ${opt.keywords || ''}`;
                                return (
                                    <CommandItem
                                        key={opt.value}
                                        value={searchValue}
                                        onSelect={() => {
                                            onChange(opt.value);
                                            setOpen(false);
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <Check className={`mr-2 h-4 w-4 ${value === opt.value ? 'opacity-100' : 'opacity-0'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate text-sm">
                                                {opt.label}
                                                {opt.badge && (
                                                    <span className="ml-2 text-[10px] uppercase font-bold bg-[#E85A24]/10 text-[#E85A24] px-1.5 py-0.5 rounded-sm">
                                                        {opt.badge}
                                                    </span>
                                                )}
                                            </div>
                                            {opt.sublabel && <div className="text-xs text-[#52525B] truncate">{opt.sublabel}</div>}
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
